
import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  opacity: number;
  size: number;
  originalSize: number;
  interactionCooldown: number;
}

interface Star {
  x: number;
  y: number;
  size: number;
  opacity: number;
}

export default function ParticleField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const starsRef = useRef<Star[]>([]);
  const animationIdRef = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const createStars = () => {
      const stars: Star[] = [];
      const starCount = Math.floor((canvas.width * canvas.height) / 8000); // More stars than particles

      for (let i = 0; i < starCount; i++) {
        stars.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 1.5 + 0.5, // Small sizes
          opacity: Math.random() * 0.6 + 0.2,
        });
      }

      starsRef.current = stars;
    };

    const createParticles = () => {
      const particles: Particle[] = [];
      const particleCount = Math.floor((canvas.width * canvas.height) / 15000);

      for (let i = 0; i < particleCount; i++) {
        const originalSize = Math.random() * 4 + 1;
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.3, // Reduced from 0.6 to 0.3 for slower movement
          vy: (Math.random() - 0.5) * 0.3, // Reduced from 0.6 to 0.3 for slower movement
          opacity: Math.random() * 0.8 + 0.2,
          size: originalSize,
          originalSize: originalSize,
          interactionCooldown: 0,
        });
      }

      particlesRef.current = particles;
    };

    const animate = () => {
      if (!ctx || !canvas) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw static stars first (background layer)
      starsRef.current.forEach((star) => {
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity * 0.4})`;
        ctx.fill();
      });

      // Update and draw animated particles
      particlesRef.current.forEach((particle) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Bounce off walls
        if (particle.x < 0 || particle.x > canvas.width) {
          particle.vx *= -0.8;
          particle.x = Math.max(0, Math.min(canvas.width, particle.x));
        }
        if (particle.y < 0 || particle.y > canvas.height) {
          particle.vy *= -0.8;
          particle.y = Math.max(0, Math.min(canvas.height, particle.y));
        }

        // Reduce interaction cooldown
        if (particle.interactionCooldown > 0) {
          particle.interactionCooldown--;
        }

        // Draw particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        const glowOpacity = particle.interactionCooldown > 0 ? 0.9 : particle.opacity;
        ctx.fillStyle = `rgba(0, 229, 255, ${glowOpacity})`;
        ctx.fill();

        // Add glow effect for recently interacted particles
        if (particle.interactionCooldown > 0) {
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size + 2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(108, 58, 255, ${particle.interactionCooldown / 60})`;
          ctx.fill();
        }
      });

      // Handle interactions and draw connections
      particlesRef.current.forEach((particle, i) => {
        particlesRef.current.slice(i + 1).forEach((otherParticle) => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // Draw connection line when particles are close
          if (distance < 120) {
            const connectionOpacity = (120 - distance) / 120;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);

            // White connection lines with reduced opacity
            if (particle.interactionCooldown > 0 || otherParticle.interactionCooldown > 0) {
              ctx.strokeStyle = `rgba(255, 255, 255, ${connectionOpacity * 0.4})`;
              ctx.lineWidth = 2;
            } else {
              ctx.strokeStyle = `rgba(255, 255, 255, ${connectionOpacity * 0.15})`;
              ctx.lineWidth = 1;
            }
            ctx.stroke();
          }

          // Particle interaction when very close
          if (distance < 50 && particle.interactionCooldown === 0 && otherParticle.interactionCooldown === 0) {
            // Calculate interaction force
            const force = (50 - distance) / 50;
            const forceX = (dx / distance) * force * 0.08; // Reduced from 0.15 to 0.08 for slower interaction
            const forceY = (dy / distance) * force * 0.08; // Reduced from 0.15 to 0.08 for slower interaction

            // Apply repulsion force
            particle.vx += forceX;
            particle.vy += forceY;
            otherParticle.vx -= forceX;
            otherParticle.vy -= forceY;

            // Add some randomness to the redirection
            const randomAngle = Math.random() * Math.PI * 2;
            const randomForce = 0.05; // Reduced from 0.1 to 0.05
            particle.vx += Math.cos(randomAngle) * randomForce;
            particle.vy += Math.sin(randomAngle) * randomForce;
            otherParticle.vx += Math.cos(randomAngle + Math.PI) * randomForce;
            otherParticle.vy += Math.sin(randomAngle + Math.PI) * randomForce;

            // Set interaction cooldown to prevent immediate re-interaction
            particle.interactionCooldown = 60;
            otherParticle.interactionCooldown = 60;

            // Increase particle size temporarily
            particle.size = Math.min(particle.originalSize * 1.5, particle.originalSize + 2);
            otherParticle.size = Math.min(otherParticle.originalSize * 1.5, otherParticle.originalSize + 2);
          }

          // Very close particles create attraction effect
          if (distance < 25 && distance > 10) {
            const attractionForce = 0.005; // Reduced from 0.01 to 0.005
            const attractionX = (-dx / distance) * attractionForce;
            const attractionY = (-dy / distance) * attractionForce;

            particle.vx += attractionX;
            particle.vy += attractionY;
            otherParticle.vx -= attractionX;
            otherParticle.vy -= attractionY;
          }
        });

        // Gradually reduce particle size back to original size
        if (particle.size > particle.originalSize) {
          particle.size = Math.max(particle.size * 0.98, particle.originalSize);
        }

        // Apply velocity damping to prevent particles from moving too fast
        const maxSpeed = 0.8; // Reduced from 1.2 to 0.8 for slower overall movement
        const speed = Math.sqrt(particle.vx * particle.vx + particle.vy * particle.vy);
        if (speed > maxSpeed) {
          particle.vx = (particle.vx / speed) * maxSpeed;
          particle.vy = (particle.vy / speed) * maxSpeed;
        }
      });

      animationIdRef.current = requestAnimationFrame(animate);
    };

    resizeCanvas();
    createStars();
    createParticles();
    animate();

    const handleResize = () => {
      resizeCanvas();
      createStars();
      createParticles();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none z-0"
      style={{ background: 'linear-gradient(225deg, #1a0b2e 0%, #0a1929 100%)' }}
    />
  );
}
